import type { AnySection } from '@wix/add-panel-common';
import { CustomScroll } from '@wix/wix-base-ui';
import classnames from 'classnames';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BIOrigin } from '../../constants/BI';
import { CATEGORY_WRAPPER_WIDTH } from '../../constants/CategoryView';
import { FedopsInteraction } from '../../constants/FedopsInteraction';
import { HelpActionInitiator, useActionsDispatcher } from '../ActionsDispatcher';
import styles from './CategoryView.scss';
import type { CategoryViewProps, CategoryViewRef } from './CategoryView.types';
import { CustomCategory } from './CustomCategory/CustomCategory';
import { useCategoryViewScroll } from './hooks/useCategoryViewScroll';
import { useTrackFirstPresetSectionImageLoad } from './hooks/useTrackFirstPresetSectionImageLoad';
import { CategorySearch } from './CategorySearch';
import { SectionViewInner } from './SectionView';
import { CategorySearchContent } from './CategorySearchContent/CategorySearch';
import { CATEGORY_SEARCH_BAR_OFFSET } from './hooks/useCategoryViewScroll/useCategoryViewScroll';

const CategoryViewComponent: ForwardRefRenderFunction<CategoryViewRef, CategoryViewProps> = (
  { setAddPanelSearchQuery, addPanelSearchQuery, category, selectedSectionId, onScroll, getExternalSectionComponent },
  ref,
) => {
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const { dispatchHelpClick, dispatchFedopsInteractionEnded } = useActionsDispatcher();

  const { id, help, groups, hasCategorySearch, showSectionsOnlyForSelectedGroup } = category;

  const hasMounted = useRef(false);

  useEffect(() => {
    if (hasMounted.current) {
      dispatchFedopsInteractionEnded({ interaction: FedopsInteraction.TabChange });
    } else {
      hasMounted.current = true;
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (addPanelSearchQuery && selectedSectionId) {
      setAddPanelSearchQuery('');
    }
  }, [id, selectedSectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = useMemo<AnySection[]>(() => {
    if (showSectionsOnlyForSelectedGroup) {
      const groupWithSelectedSection = groups.find((g) =>
        g.sections.find((section) => section.systemId === selectedSectionId),
      );
      if (groupWithSelectedSection) return groupWithSelectedSection.sections;
    }
    return groups.flatMap(({ sections: groupSections }) => groupSections);
  }, [groups, selectedSectionId, showSectionsOnlyForSelectedGroup]);

  useTrackFirstPresetSectionImageLoad(sections);

  const {
    customScrollRef,
    getScrollTop,
    isScrollInProcess,
    scrollTo,
    scrollToSectionTop,
    handleScroll,
    registerSectionElement,
    contentWrapperRef,
  } = useCategoryViewScroll({
    sections,
    onScroll,
    hasCategorySearch,
  });

  const handleCategoryScroll = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement)?.scrollTop > 3) {
      if (!isScrolled) {
        setIsScrolled(true);
      }
    } else if (isScrolled) {
      setIsScrolled(false);
    }
    handleScroll(e);
  };

  useImperativeHandle<CategoryViewRef, CategoryViewRef>(
    ref,
    () => ({
      getScrollTop,
      isScrollInProcess,
      scrollTo,
      scrollToSectionTop,
    }),
    [getScrollTop, scrollTo, scrollToSectionTop, isScrollInProcess],
  );

  const handleHelpClick = useCallback(() => {
    dispatchHelpClick({
      helpId: help ? help.helpId : '',
      categoryId: id,
      initiator: HelpActionInitiator.CategoryViewHeader,
      biHelpParams: {
        origin: BIOrigin.AddPanel,
        component: id,
        panel_name: BIOrigin.AddPanel,
        learn_more: false,
      },
    });
  }, [dispatchHelpClick, help, id]);

  return (
    <section
      className={classnames(styles.categoryViewWrapper)}
      data-hook="add-panel-category-view"
      style={{ width: CATEGORY_WRAPPER_WIDTH }}
    >
      <div className={classnames(styles.categoryView)}>
        {category.hasCategorySearch ? (
          <CategorySearch isScrolled={isScrolled} onQueryChange={setAddPanelSearchQuery} />
        ) : null}
        <CustomScroll
          ref={customScrollRef}
          onScroll={handleCategoryScroll}
          heightRelativeToParent={category.hasCategorySearch ? `calc(100% - ${CATEGORY_SEARCH_BAR_OFFSET}px)` : '100%'}
          addScrolledClass
          // freezePosition={isDragging} // TODO: implement it
        >
          <div ref={contentWrapperRef}>
            {category.isCustom ? (
              <CustomCategory category={category} />
            ) : addPanelSearchQuery ? (
              <CategorySearchContent
                query={addPanelSearchQuery}
                resetQuery={() => setAddPanelSearchQuery('')}
                getExternalSectionComponent={getExternalSectionComponent}
              />
            ) : (
              <div className={styles.sections} data-hook="sections-wrapper">
                {sections.map((section, index) => (
                  <SectionViewInner
                    key={section.systemId}
                    addPanelSearchQuery={addPanelSearchQuery}
                    setAddPanelSearchQuery={setAddPanelSearchQuery}
                    className={styles.section}
                    section={section}
                    getExternalSectionComponent={getExternalSectionComponent}
                    notifyLayoutReady={registerSectionElement}
                    index={index}
                  />
                ))}
              </div>
            )}
          </div>
        </CustomScroll>
      </div>
    </section>
  );
};

export const CategoryView = forwardRef(CategoryViewComponent);
